import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import CustomAlert from '../../components/CustomAlert/CustomAlert';
import { API_BASE_URL } from '../../config';
import { 
    FiX, FiPlusCircle, FiEdit, FiTrash2, FiSave, FiCalendar, 
    FiMapPin, FiUser, FiFileText, FiActivity, FiDollarSign, 
    FiBarChart2, FiTrendingUp, FiLayers, FiFilter 
} from 'react-icons/fi';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement,
    Filler // Importado para preenchimento de área se necessário
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import styles from './ReceitasModal.module.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    LineElement,
    PointElement,
    Filler
);

const ReceitasModal = ({ equipeId, nomeEquipe, onClose }) => {
    const [receitas, setReceitas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [currentReceita, setCurrentReceita] = useState(null);
    const [clientes, setClientes] = useState([]);
    const [loadingClientes, setLoadingClientes] = useState(false);
    
    // Estado para o ano selecionado (padrão para o ano atual)
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const [formData, setFormData] = useState({
        data: '',
        localRealizado: '',
        clienteId: '',
        numeroNota: '',
        quantidade: '',
        valorTotal: '',
    });

    const [alert, setAlert] = useState({
        show: false,
        type: 'info',
        title: '',
        text: '',
        showCancelButton: false,
        onConfirm: null,
        onCancel: () => setAlert(prev => ({ ...prev, show: false })),
        autoClose: 0,
    });

    const token = localStorage.getItem('token');
    
    // --- FUNÇÕES AUXILIARES ---
    const formatCurrency = (value) => {
        const numericValue = parseFloat(value) || 0;
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(numericValue);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset()); 
        return date.toLocaleDateString('pt-BR');
    };

    const handleCloseForm = useCallback(() => {
        setIsFormOpen(false);
        setCurrentReceita(null);
    }, []);

    const showAlert = useCallback((options) => {
        setAlert({
            show: true,
            type: options.icon,
            title: options.title,
            text: options.text,
            showCancelButton: options.showCancelButton || false,
            onConfirm: options.confirmAction,
            onCancel: options.cancelAction || (() => setAlert(prev => ({ ...prev, show: false }))),
            autoClose: options.autoClose || 0,
        });
    }, []);

    // --- FETCHES ---
    const fetchClientes = useCallback(async () => {
        setLoadingClientes(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/colheita/clientes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClientes(response.data);
        } catch (err) {
            console.error("Erro ao buscar clientes:", err);
        } finally {
            setLoadingClientes(false);
        }
    }, [token]);

    const handleCadastrarNovoCliente = useCallback(async (nome) => {
        if (!nome || typeof nome !== 'string' || nome.trim() === '') return null;
        try {
            const response = await axios.post(`${API_BASE_URL}/api/colheita/clientes`, { nome: nome.trim() }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const novoCliente = { id: response.data.id, nome: response.data.nome };
            setClientes(prev => [...prev, novoCliente].sort((a, b) => a.nome.localeCompare(b.nome)));
            showAlert({ icon: 'success', title: 'Cliente Cadastrado!', text: `O cliente "${nome.trim()}" foi salvo.`, autoClose: 2000 });
            return novoCliente;
        } catch (err) {
            console.error("Erro ao cadastrar cliente:", err);
            showAlert({ icon: 'error', title: 'Falha no Cadastro', text: 'Erro ao salvar cliente.' });
            return null;
        }
    }, [token, showAlert]);

    const handleClientChange = useCallback(async (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, clienteId: value }));
        
        if (value === 'novo_cliente') {
            const { value: novoNome } = await Swal.fire({
                title: 'Cadastrar Novo Cliente',
                input: 'text',
                inputLabel: 'Nome do Cliente',
                inputPlaceholder: 'Digite o nome...',
                showCancelButton: true,
                confirmButtonText: 'Salvar',
                cancelButtonText: 'Cancelar',
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#6c757d',
                inputValidator: (value) => {
                    if (!value) return 'Você precisa digitar um nome!';
                }
            });

            if (novoNome) {
                const novoCliente = await handleCadastrarNovoCliente(novoNome);
                if (novoCliente) {
                    setFormData(prev => ({ ...prev, clienteId: novoCliente.id.toString() }));
                } else {
                    setFormData(prev => ({ ...prev, clienteId: '' }));
                }
            } else {
                setFormData(prev => ({ ...prev, clienteId: '' }));
            }
        }
    }, [handleCadastrarNovoCliente]);

    const fetchReceitas = useCallback(async () => {
        setLoading(true);
        try {
            if (!token) throw new Error("Usuário não autenticado.");
            const response = await axios.get(`${API_BASE_URL}/api/colheita/equipes/${equipeId}/receitas`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReceitas(response.data);
            
            // Se houver receitas, tenta ajustar o ano selecionado para o ano da receita mais recente se o atual não existir
            if (response.data.length > 0) {
                const years = new Set(response.data.map(r => new Date(r.data).getFullYear()));
                if (!years.has(selectedYear)) {
                    // Pega o ano mais recente disponível
                    const maxYear = Math.max(...Array.from(years));
                    setSelectedYear(maxYear);
                }
            }
        } catch (err) {
            console.error(err);
            showAlert({ icon: 'error', title: 'Erro', text: 'Erro ao carregar receitas.' });
        } finally {
            setLoading(false);
        }
    }, [equipeId, token, showAlert, selectedYear]);

    // --- CRUD ---
    const handleDelete = useCallback(async (receitaId) => {
        showAlert({
            icon: 'question',
            title: 'Tem certeza?',
            text: "A receita será deletada permanentemente.",
            showCancelButton: true,
            confirmAction: async () => {
                setAlert(prev => ({ ...prev, show: false }));
                setLoading(true);
                try {
                    await axios.delete(`${API_BASE_URL}/api/colheita/receitas/${receitaId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    await fetchReceitas();
                    showAlert({ icon: 'success', title: 'Deletado!', text: 'Receita removida.', autoClose: 2000 });
                } catch (err) {
                    console.error(err);
                    showAlert({ icon: 'error', title: 'Erro', text: 'Erro ao deletar receita.' });
                } finally {
                    setLoading(false);
                }
            }
        });
    }, [token, fetchReceitas, showAlert]);

    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);

        const payload = {
            equipeId: equipeId,
            ...formData,
            clienteId: parseInt(formData.clienteId),
        };
        
        if (!payload.clienteId) {
             showAlert({ icon: 'warning', title: 'Atenção', text: 'Selecione um cliente.' });
             setLoading(false);
             return;
        }

        payload.quantidade = parseFloat(payload.quantidade);
        payload.valorTotal = parseFloat(payload.valorTotal);
        
        try {
            let message = '';
            if (currentReceita) {
                await axios.put(`${API_BASE_URL}/api/colheita/receitas/${currentReceita.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message = "Receita atualizada!";
            } else {
                await axios.post(`${API_BASE_URL}/api/colheita/receitas`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message = "Receita criada!";
            }
            await fetchReceitas();
            handleCloseForm();
            showAlert({ icon: 'success', title: 'Sucesso!', text: message, autoClose: 2000 });
        } catch (err) {
            console.error(err);
            showAlert({ icon: 'error', title: 'Erro', text: 'Erro ao salvar.' });
        } finally {
            setLoading(false);
        }
    }, [equipeId, token, currentReceita, formData, fetchReceitas, handleCloseForm, showAlert]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        if (name === 'clienteId') handleClientChange(e);
        else setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleOpenForm = (receita = null) => {
        setCurrentReceita(receita);
        if (receita) {
            setFormData({
                data: receita.data.split('T')[0],
                localRealizado: receita.local_realizado,
                clienteId: receita.cliente_id.toString(),
                numeroNota: receita.numero_nota || '',
                quantidade: receita.quantidade,
                valorTotal: receita.valor_total,
            });
        } else {
            setFormData({
                data: new Date().toISOString().split('T')[0],
                localRealizado: '',
                clienteId: '',
                numeroNota: '',
                quantidade: '',
                valorTotal: '',
            });
        }
        setIsFormOpen(true);
    };

    useEffect(() => {
        fetchReceitas();
        fetchClientes();
    }, [fetchReceitas, fetchClientes]);

    // --- LÓGICA DE DADOS POR ANO (MEMO) ---
    
    // 1. Extrair anos disponíveis para o seletor
    const availableYears = useMemo(() => {
        const years = new Set(receitas.map(r => new Date(r.data).getFullYear()));
        // Adiciona o ano atual se não existir, para não quebrar a UI se não tiver dados
        years.add(new Date().getFullYear());
        return Array.from(years).sort((a, b) => b - a);
    }, [receitas]);

    // 2. Processar dados para os gráficos com base no ano selecionado
    const { filteredTotalQuantidade, filteredTotalValor, monthlyLabels, monthlyValuesVA, monthlyValuesTon } = useMemo(() => {
        // Inicializa arrays para os 12 meses
        const monthsData = Array.from({ length: 12 }, () => ({ va: 0, ton: 0 }));
        const monthNames = [
            'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 
            'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
        ];

        let totalQtd = 0;
        let totalVal = 0;

        // Filtra e agrega
        receitas.forEach(r => {
            // Ajuste de timezone simples para extrair ano/mês corretamente da string ISO ou Date
            const d = new Date(r.data);
            // Corrige timezone offset localmente para garantir que o mês/ano corresponda à data visual
            d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
            
            const rYear = d.getFullYear();
            const rMonth = d.getMonth(); // 0 a 11

            if (rYear === parseInt(selectedYear)) {
                const qtd = parseFloat(r.quantidade) || 0;
                const val = parseFloat(r.valor_total) || 0;

                monthsData[rMonth].va += val;
                monthsData[rMonth].ton += qtd;

                totalQtd += qtd;
                totalVal += val;
            }
        });

        return {
            filteredTotalQuantidade: totalQtd,
            filteredTotalValor: totalVal,
            monthlyLabels: monthNames,
            monthlyValuesVA: monthsData.map(m => m.va),
            monthlyValuesTon: monthsData.map(m => m.ton)
        };
    }, [receitas, selectedYear]);


    // --- CONFIGURAÇÕES CHART.JS ---
    const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1e293b',
                bodyColor: '#475569',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                titleFont: { size: 14, weight: 'bold' },
                padding: 10,
                displayColors: false,
            },
        },
        scales: {
            x: { grid: { display: false }, ticks: { color: '#64748b', font: { size: 11 } } },
            y: { ticks: { color: '#64748b', font: { size: 11 } }, grid: { color: '#f1f5f9' }, border: { display: false } },
        },
    };

    // Gráfico de Barras (Receita R$)
    const barDataVA = {
        labels: monthlyLabels,
        datasets: [{
            label: 'Receita (R$)',
            data: monthlyValuesVA,
            backgroundColor: '#16a34a',
            borderRadius: 6,
            barThickness: 'flex',
            maxBarThickness: 40,
        }],
    };

    const barOptionsVA = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            tooltip: {
                ...commonOptions.plugins.tooltip,
                callbacks: { label: (ctx) => ` Receita: ${formatCurrency(ctx.parsed.y)}` }
            }
        },
        scales: {
            ...commonOptions.scales,
            y: { ...commonOptions.scales.y, ticks: { callback: (v) => formatCurrency(v).split('\u00A0')[0] + '...' } } // Abreviação simples
        }
    };
    
    // Gráfico de Linha (Produção Ton)
    const lineDataTon = {
        labels: monthlyLabels,
        datasets: [{
            label: 'Produção (Ton)',
            data: monthlyValuesTon,
            borderColor: '#2563eb', // Azul para diferenciar visualmente da receita
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
            pointBackgroundColor: '#2563eb',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            tension: 0.3, // Curva suave
            fill: true,
        }],
    };

    const lineOptionsTon = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            tooltip: {
                ...commonOptions.plugins.tooltip,
                callbacks: { label: (ctx) => ` Produção: ${ctx.parsed.y.toFixed(2)} Ton` }
            }
        },
        scales: {
            ...commonOptions.scales,
            y: { ...commonOptions.scales.y, ticks: { callback: (v) => `${v}t` } }
        }
    };

    return (
        <div className={styles.modalBackdrop}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2><FiDollarSign /> Receitas de Colheita - {nomeEquipe}</h2>
                    <button onClick={onClose} className={styles.modalCloseBtn} disabled={loading || alert.show}><FiX size={24} /></button>
                </div>
                
                <div className={styles.modalBody}>
                    {loading && <p className={styles.loadingMsg}>Carregando...</p>}
                    
                    {/* Sumário Dinâmico (Baseado no Ano Selecionado) */}
                    <div className={styles.summaryBar}>
                        <p>Total {selectedYear} (Ton): <strong>{filteredTotalQuantidade.toFixed(2)}</strong></p>
                        <p>Total {selectedYear} (VA): <strong>{formatCurrency(filteredTotalValor)}</strong></p>
                    </div>

                    <button 
                        onClick={() => handleOpenForm()} 
                        className={styles.btnAddReceita}
                        disabled={isFormOpen || loading}
                    >
                        <FiPlusCircle /> {currentReceita ? 'Editando Receita' : 'Nova Receita'}
                    </button>
                    
                    {isFormOpen ? (
                         <form onSubmit={handleFormSubmit} className={styles.receitaForm}>
                            <h4>{currentReceita ? 'Editar Receita' : 'Cadastrar Nova Receita'}</h4>
                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label><FiCalendar /> Data:</label>
                                    <input type="date" name="data" value={formData.data} onChange={handleFormChange} required />
                                </div>
                                <div className={styles.formGroup}>
                                    <label><FiMapPin /> Local Realizado:</label>
                                    <input type="text" name="localRealizado" value={formData.localRealizado} onChange={handleFormChange} required />
                                </div>
                                
                                <div className={styles.formGroup}>
                                    <label><FiUser /> Cliente:</label>
                                    {loadingClientes ? (
                                        <p>Carregando clientes...</p>
                                    ) : (
                                        <select name="clienteId" value={formData.clienteId} onChange={handleFormChange} required>
                                            <option value="">-- Selecione ou Cadastre --</option>
                                            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                                            <option value="novo_cliente" style={{ fontWeight: 'bold', color: '#16a34a' }}>+ Novo Cliente</option>
                                        </select>
                                    )}
                                </div>
                                
                                <div className={styles.formGroup}>
                                    <label><FiFileText /> N° Nota:</label>
                                    <input type="text" name="numeroNota" value={formData.numeroNota} onChange={handleFormChange} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label><FiActivity /> Quantidade (Ton):</label>
                                    <input type="number" name="quantidade" value={formData.quantidade} onChange={handleFormChange} step="0.01" required />
                                </div>
                                <div className={styles.formGroup}>
                                    <label><FiDollarSign /> Valor Total (VA):</label>
                                    <input type="number" name="valorTotal" value={formData.valorTotal} onChange={handleFormChange} step="0.01" required />
                                </div>
                            </div>
                            <div className={styles.formActions}>
                                <button type="submit" disabled={loading} className={styles.btnSave}><FiSave /> Salvar</button>
                                <button type="button" onClick={handleCloseForm} className={styles.btnCancel}>Cancelar</button>
                            </div>
                        </form>
                    ) : (
                        <>
                            {/* SEÇÃO DE GRÁFICOS COM FILTRO DE ANO */}
                            {receitas.length === 0 ? (
                                <p className={styles.emptyState}>Nenhuma receita registrada. Cadastre a primeira!</p>
                            ) : (
                                <div className={styles.chartsSection}>
                                    <div className={styles.yearFilterContainer}>
                                        <label htmlFor="yearSelect"><FiFilter /> Visualizando Ano:</label>
                                        <select 
                                            id="yearSelect"
                                            value={selectedYear} 
                                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                            className={styles.yearSelect}
                                        >
                                            {availableYears.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={styles.dashboardGrid}>
                                        <div className={styles.chartCard}>
                                            <h5 className={styles.chartTitle}>
                                                <FiBarChart2 /> Faturamento Mensal ({selectedYear})
                                            </h5>
                                            <div style={{ height: 250 }}>
                                                <Bar options={barOptionsVA} data={barDataVA} />
                                            </div>
                                        </div>

                                        <div className={styles.chartCard}>
                                            <h5 className={styles.chartTitle} style={{ color: '#2563eb' }}>
                                                <FiTrendingUp style={{ color: '#2563eb' }} /> Produção Mensal ({selectedYear})
                                            </h5>
                                            <div style={{ height: 250 }}>
                                                <Line options={lineOptionsTon} data={lineDataTon} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TABELA DE REGISTROS */}
                            <h3 className={styles.sectionTitleList}><FiLayers /> Histórico Completo de Receitas</h3>
                            
                            {receitas.length > 0 && (
                                <div className={styles.receitasList}>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Local</th>
                                                <th>Cliente</th>
                                                <th>Nº Nota</th>
                                                <th>Qtd (Ton)</th>
                                                <th>Valor (VA)</th>
                                                <th>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {receitas.map(receita => (
                                                <tr key={receita.id}>
                                                    <td>{formatDate(receita.data)}</td>
                                                    <td>{receita.local_realizado}</td>
                                                    <td>{receita.cliente_nome || 'N/A'}</td>
                                                    <td>{receita.numero_nota || '-'}</td>
                                                    <td style={{ fontWeight: 600 }}>{parseFloat(receita.quantidade).toFixed(2)}</td>
                                                    <td style={{ color: '#16a34a', fontWeight: 600 }}>{formatCurrency(receita.valor_total)}</td>
                                                    <td className={styles.actionsCell}>
                                                        <button onClick={() => handleOpenForm(receita)} className={styles.btnIconEdit}><FiEdit /></button>
                                                        <button onClick={() => handleDelete(receita.id)} className={styles.btnIconDelete}><FiTrash2 /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>
                
                <CustomAlert 
                    show={alert.show}
                    type={alert.type}
                    title={alert.title}
                    text={alert.text}
                    showCancelButton={alert.showCancelButton}
                    onConfirm={alert.onConfirm}
                    onCancel={alert.onCancel}
                    autoClose={alert.autoClose}
                />
            </div>
        </div>
    );
};

export default ReceitasModal;